// library
import { 
    FC, 
    useState, 
    useEffect, 
    useLayoutEffect, 
    useReducer, 
    useMemo, 
    useCallback 
} from "react";
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from "dayjs";
// material
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Grid,
} from '@mui/material';
// type
import { Feature } from "geojson";
import { Event } from "../types/event";
import { Point } from "geojson";
// common
import Utils from "../common/utils";
import { Divider } from "@mui/material";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': { padding: theme.spacing(2), },
    '& .MuiDialogActions-root': { padding: theme.spacing(1), },
}));

// wetherApi基本情報
const WETHER_API_KEY = "9f3b314cc519c2a01607f63f809ce76c";
const WETHER_BASE_URL = "https://api.openweathermap.org/data/2.5/forecast";

const MapEventDialog: FC<any> = ( props: { config: any, isOpenDialog: boolean, setIsOpenDialog: any, currentFeature: Feature }) => {
    const { config, isOpenDialog, setIsOpenDialog, currentFeature } = props;
    const currentEvent = currentFeature?.properties as Event;

    const [ weatherData, setWeatherData ] = useState<any>(null);
    const [ isWeather, setIsWeather ] = useState(false);
    
    const image = `${config.imageUrl}${currentEvent?.image}.png`;

    // GoogleMapを表示
    const geometry = currentFeature?.geometry;
    let coordinates: number[] = []
    if (geometry?.type === 'Point') {
        coordinates = (geometry as Point).coordinates;
    }

    // googleMapへのリンク
    const googleMapsUrl = `${config.googleMapUrl}q=${coordinates[1]},${coordinates[0]}&z=15`;

    useEffect(() => {

        if (!currentEvent?.startDate || !currentEvent?.endDate) return;

        const pattern = Utils.eventDatePattern(currentEvent?.startDate, currentEvent?.endDate);

        if (pattern !== undefined) {
            setIsWeather(pattern < 2);
        }
    
        if (isOpenDialog && coordinates.length === 2 && isWeather) {
            const fetchWeather = async () => {
                try {
                    const response = await fetch(`${WETHER_BASE_URL}?lat=${coordinates[1]}&lon=${coordinates[0]}&appid=${WETHER_API_KEY}&units=metric&lang=ja`);
                    if (!response.ok) throw new Error(`HTTPエラー: ${response.status}`);
                    const data = await response.json();
                    setWeatherData(data);
                } catch (error) {
                    console.error("天気情報の取得エラー:", error);
                }
            };
            fetchWeather();
        }
    }, [isOpenDialog, coordinates, isWeather]);

    // ダイアログの閉じる押下時
    const handleClose = () => {
        // 閉じる
        setIsOpenDialog(false);
    }

    const getWeatherForTime = (weatherList: any = [], targetTimes: string[]) => {
        return targetTimes.map(targetTime =>
            weatherList?.find((entry: any) => entry.dt_txt.includes(targetTime))
        ).find(entry => entry) || null; // 最初に見つかったデータ、なければ null
    };
    

    const getWeatherForDateRange = (weatherData: any, startDate: string, endDate: string) => {
        const today = dayjs().format("YYYY-MM-DD"); // 今日の日付を取得
        const adjustedStartDate = dayjs(startDate).isBefore(today) ? today : startDate; // 過去の日付なら今日を開始日に変更
    
        // 日付ごとに天気データを分類
        const groupedWeather = weatherData?.list?.reduce((acc: any, entry: any) => {
            const date = dayjs(entry.dt_txt).format("YYYY-MM-DD"); // 正確な日付フォーマット
            if (dayjs(date).isBetween(adjustedStartDate, endDate, null, "[]")) { // `startDate` から `endDate` の範囲
                if (!acc[date]) acc[date] = [];
                acc[date].push(entry);
            }
            return acc;
        }, {}) || {};
    
        // 各日付の午前・午後の天気を取得
        return Object.keys(groupedWeather).map(date => ({
            date,
            morningWeather: getWeatherForTime(groupedWeather[date] || [], ["09:00:00", "12:00:00"]),
            afternoonWeather: getWeatherForTime(groupedWeather[date] || [], ["15:00:00", "18:00:00"])
        }));
    };

    // ✅ ダイアログ表示時に使用
    const displayWeather = getWeatherForDateRange(weatherData, currentEvent?.startDate, currentEvent?.endDate);

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={isOpenDialog}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {currentEvent?.title}
                {/* {currentFeature.properties?.title} */}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
            <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Typography gutterBottom>
                    <img
                        src="./icons/icon-place.svg"
                        height={20}
                    />
                    {` 場所 `}
                    {` ${currentEvent?.place}`}
                </Typography>
                <Divider/>
                <Typography gutterBottom>
                    <img
                        className="pictogram"
                        src="./icons/icon-date.svg"
                        height={20}
                    />
                    {` 日時 `}
                    {` ${Utils.eventDateString(currentEvent?.startDate, currentEvent?.endDate)}`}
                </Typography>
                <Divider/>
                { 
                    currentEvent?.startTime && currentEvent?.endTime &&
                        <Typography>
                            <img
                                className="pictogram"
                                src="./icons/icon-time.svg"
                                height={20}
                            />
                            {` 時刻 `}
                            {` ${currentEvent?.startTime}～${currentEvent?.endTime}`}
                        </Typography>
                }
                {
                    currentEvent?.startTime && currentEvent?.endTime &&
                        <Divider/>
                }
                <Typography gutterBottom>
                    <img
                        className="pictogram"
                        src="./icons/icon-description.svg"
                        height={20}
                    />
                    {` 詳細`}
                    <br></br>
                    {currentEvent?.description}
                </Typography>
                    <div className="event-dialog-image">
                        <img 
                            src={image}
                            alt={currentEvent?.title}
                        />
                    </div>

                {
                    currentEvent?.url &&
                        <Typography gutterBottom>
                            
                            <img
                                className="pictogram"
                                src="./icons/icon-link.svg"
                                height={20}
                            />
                            <a href={currentEvent?.url} target="_blank">{currentEvent?.title}</a><br/>
                            ※ 正確な情報はリンク先でご確認ください。
                        </Typography>
                }
                {displayWeather.map(({ date, morningWeather, afternoonWeather }, index) => (
                    <div key={index}>
                        {(morningWeather || afternoonWeather) &&

                            (<>
                                <Divider/>
                                <Typography variant="h6">{dayjs(date).format("M月D日(ddd)")}の天気</Typography>
                            </>)
                        }

                        <Grid container spacing={2}>
                            {/* 午前（左側表示） */}
                            {morningWeather && (
                                <Grid item xs={6}>
                                    <Typography>午前</Typography>
                                    <img
                                        src={`https://openweathermap.org/img/w/${morningWeather.weather[0].icon}.png`}
                                        height={40}
                                        alt={morningWeather.weather[0].description}
                                        style={{ float: "left" }}
                                    />
                                    <Typography>{morningWeather.weather[0].description}</Typography>
                                    <Typography>{`${Math.round(morningWeather.main.temp)}°C`}</Typography>
                                </Grid>
                            )}

                            {/* 午後（右側表示） */}
                            {afternoonWeather && (
                                <Grid item xs={6}>
                                    <Typography>午後</Typography>
                                    <img
                                        src={`https://openweathermap.org/img/w/${afternoonWeather.weather[0].icon}.png`}
                                        height={40}
                                        alt={afternoonWeather.weather[0].description}
                                        style={{ float: "left" }}
                                    />
                                    <Typography>{afternoonWeather.weather[0].description}</Typography>
                                    <Typography>{`${Math.round(afternoonWeather.main.temp)}°C`}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </div>
                ))}
                <Divider/>
                <Typography gutterBottom>
                    <img
                        className="pictogram"
                        src="./icons/icon-link.svg"
                        height={20}
                    />
                    <a href={googleMapsUrl} target="_blank">Google Mapで地図を開く</a>
                </Typography>

            </DialogContent>
            <Button autoFocus onClick={handleClose}>
                閉じる
            </Button>
        </BootstrapDialog>
    );
}

export default MapEventDialog;